import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { loginUser, registerUser } from '../utils/api';
import PasswordStrengthMeter from '../components/common/PasswordStrengthMeter';
import { Scale, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [mode, setMode] = useState('login');
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    email: '', password: '', first_name: '', last_name: '',
    firm_name: '', phone: '', confirm_password: ''
  });

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await loginUser(form.email, form.password);
      login(result.token, result.user, result.refresh_token);
      if (result.password_expired) {
        toast.error('Your password has expired. Please update it now.');
        navigate('/profile');
      } else if (result.password_expiring_soon) {
        toast('Your password expires in ' + result.password_days_remaining + ' days. Please update it soon.', { duration: 5000 });
        navigate('/');
      } else {
        toast.success('Welcome back!');
        navigate('/');
      }
    } catch (err) {
      toast.error(err.message || 'Invalid credentials');
    } finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const result = await registerUser({
        email: form.email, password: form.password,
        first_name: form.first_name, last_name: form.last_name,
        firm_name: form.firm_name, phone: form.phone
      });
      login(result.token, result.user, result.refresh_token);
      toast.success('Firm account created!');
      navigate('/');
    } catch (err) {
      toast.error(err.message || 'Registration failed');
    } finally { setLoading(false); }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--zeus-black)',
    }}>
      <div style={{ width: '100%', maxWidth: '420px', padding: '32px 24px' }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '36px' }}>
          <div style={{
            width: '52px', height: '52px', margin: '0 auto 14px',
            background: 'var(--zeus-gold-dim)', borderRadius: '14px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Scale size={24} style={{ color: 'var(--zeus-gold)' }} />
          </div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', fontWeight: 400, margin: 0 }}>
            Zeus<span style={{ color: 'var(--zeus-gold)' }}>CMS</span>
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem', marginTop: '6px' }}>
            Case Management for Injury Law
          </p>
        </div>

        {/* Card */}
        <div style={{
          background: 'var(--zeus-navy)', borderRadius: '16px',
          border: '1px solid var(--zeus-border)', padding: '28px',
        }}>

          {/* ── LOGIN ── */}
          {mode === 'login' && (
            <>
              <h2 style={{ fontSize: '1.15rem', fontFamily: 'var(--font-display)', margin: '0 0 20px', textAlign: 'center' }}>Sign In</h2>

              <form onSubmit={handleLogin}>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input className="form-input" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="you@firm.com" required />
                </div>
                <div className="form-group">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label className="form-label">Password</label>
                    <button onClick={function() { navigate('/reset-password'); }} type="button" style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.75rem', fontFamily: 'var(--font-body)', padding: 0 }}>
                      Forgot password?
                    </button>
                  </div>
                  <input className="form-input" type="password" value={form.password} onChange={e => set('password', e.target.value)} required />
                </div>
                <button className="btn btn-primary btn-lg" type="submit" disabled={loading} style={{ width: '100%', justifyContent: 'center', marginTop: '4px' }}>
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>
              </form>

              <div style={{ marginTop: '20px', textAlign: 'center', borderTop: '1px solid var(--zeus-border)', paddingTop: '16px' }}>
                <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>New to ZeusCMS? </span>
                <button onClick={() => { setMode('register'); setStep(1); }} style={{ background: 'none', border: 'none', color: 'var(--zeus-gold)', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, fontFamily: 'var(--font-body)' }}>
                  Create an account
                </button>
              </div>
            </>
          )}

          {/* ── REGISTER STEP 1 ── */}
          {mode === 'register' && step === 1 && (
            <>
              <h2 style={{ fontSize: '1.15rem', fontFamily: 'var(--font-display)', margin: '0 0 4px', textAlign: 'center' }}>Create Your Firm</h2>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', textAlign: 'center', marginBottom: '20px' }}>Free during early access</p>

              <div className="form-group">
                <label className="form-label">Law Firm Name *</label>
                <input className="form-input" value={form.firm_name} onChange={e => set('firm_name', e.target.value)} placeholder="Henderson Law Group" required />
              </div>
              <div className="form-group">
                <label className="form-label">Firm Phone</label>
                <input className="form-input" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="(713) 555-0100" />
              </div>

              <button className="btn btn-primary btn-lg" onClick={() => { if (!form.firm_name) { toast.error('Firm name required'); return; } setStep(2); }} style={{ width: '100%', justifyContent: 'center', marginTop: '4px' }}>
                Continue <ArrowRight size={16} />
              </button>

              <div style={{ marginTop: '16px', textAlign: 'center' }}>
                <button onClick={() => setMode('login')} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.82rem', fontFamily: 'var(--font-body)' }}>
                  Already have an account? Sign in
                </button>
              </div>
            </>
          )}

          {/* ── REGISTER STEP 2 ── */}
          {mode === 'register' && step === 2 && (
            <>
              <h2 style={{ fontSize: '1.15rem', fontFamily: 'var(--font-display)', margin: '0 0 4px', textAlign: 'center' }}>Your Account</h2>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', textAlign: 'center', marginBottom: '20px' }}>You will be the firm owner</p>

              <form onSubmit={handleRegister}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <div className="form-group">
                    <label className="form-label">First Name *</label>
                    <input className="form-input" value={form.first_name} onChange={e => set('first_name', e.target.value)} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Last Name *</label>
                    <input className="form-input" value={form.last_name} onChange={e => set('last_name', e.target.value)} required />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input className="form-input" type="email" value={form.email} onChange={e => set('email', e.target.value)} required />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <div className="form-group">
                    <label className="form-label">Password *</label>
                    <input className="form-input" type="password" value={form.password} onChange={e => set('password', e.target.value)} required minLength={12} />
                    <PasswordStrengthMeter password={form.password} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Confirm *</label>
                    <input className="form-input" type="password" value={form.confirm_password} onChange={e => set('confirm_password', e.target.value)} required />
                  </div>
                </div>

                <button className="btn btn-primary btn-lg" type="submit" disabled={loading} style={{ width: '100%', justifyContent: 'center', marginTop: '4px' }}>
                  {loading ? 'Creating...' : 'Create Account'}
                </button>
              </form>

              <div style={{ marginTop: '16px', textAlign: 'center' }}>
                <button onClick={() => setStep(1)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.82rem', fontFamily: 'var(--font-body)' }}>
                  Back
                </button>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <p style={{ textAlign: 'center', fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '20px' }}>
          PI & Workers' Comp case management
        </p>
      </div>
    </div>
  );
}
